import { useMemo } from 'react';

import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  styled,
} from '@mui/material';

import { useParams } from 'react-router';

import {
  useGetSharedScenario,
} from '@app-velocity/modules/scenarios';
import { useI18n } from '@app-velocity/i18n';
import { PartialData, isPageData } from '@app-velocity/common';

import {
  SinglePathView,
  useReportInternalContex,
} from '@app-velocity/libs/report';

const StyledDebugContainer = styled('div')({
  flex: '0 0 25%',
  whiteSpace: 'nowrap',
});

const StyledListSubheader = styled(ListSubheader)({
  padding: 0,
  lineHeight: 2,
  fontWeight: 600,
});

export type DebugProps = {
  view: SinglePathView;
};

export const Debug = ({ view }: DebugProps) => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { t } = useI18n();
  const { featurePreview } = useReportInternalContex();
  const { data: scenario } = useGetSharedScenario(shareToken || '', 'report');

  const page = scenario?.prototypePages?.find((p) => p.id === view.pageId);

  const viewPartials = useMemo(() => {
    const { partials } = view;

    const allViewPartialsIds = partials.map((p) => p.id);

    const filteredPartials = (scenario?.prototypePartials || []).filter(
      (prototypePartial) => allViewPartialsIds.includes(prototypePartial.id)
    );

    return filteredPartials;
  }, [scenario, view]);

  const pageActions = useMemo(() => {
    if (!page) return [];
    if (!isPageData(page.data)) return [];
    const actions = page.data.actions;

    return actions;
  }, [scenario, page]);

  if (!featurePreview) return null;

  return (
    <StyledDebugContainer>
      {t('prototypes:available_actions')}
      <List>
        {pageActions.map((action) => (
          <ListItem key={action.id}>
            <ListItemText
              primary={t('prototypes:action_debug.short', {
                action: action.action,
                trigger: action.trigger,
              })}
              secondary={
                action.triggerData
                  ? t('prototypes:action_debug.after_seconds', {
                      seconds: action.triggerData.timeout,
                    })
                  : null
              }
            />
          </ListItem>
        ))}
        {viewPartials.map((partial) => (
          <>
            <StyledListSubheader>
              {(partial.data as PartialData).name}
            </StyledListSubheader>
            {(partial.data as PartialData).actions.map((action) => (
              <ListItem key={action.id}>
                <ListItemText
                  primary={t('prototypes:action_debug.short', {
                    action: action.action,
                    trigger: action.trigger,
                  })}
                />
              </ListItem>
            ))}
          </>
        ))}
      </List>
    </StyledDebugContainer>
  );
};
